import {EmptyExec, Errors, isDefined, Option, Provider, Terminable, Terminator} from "@opendaw/lib-std"
import {AnimationFrame, Browser, Events} from "@opendaw/lib-dom"
import {LogBuffer} from "@/errors/LogBuffer.ts"
import {ErrorLog} from "@/errors/ErrorLog.ts"
import {ErrorInfo} from "@/errors/ErrorInfo.ts"
import {Surface} from "@/ui/surface/Surface.tsx"
import {Dialogs} from "@/ui/components/dialogs.tsx"
import {BuildInfo} from "@/BuildInfo"

const ExtensionPatterns = ["script-src blocked eval", "extension", "chrome-extension://", "blocked by CSP", "Zotero Connector", "hintMode", "handleHint",
    "user-script:"]
const IgnoredErrors = [
    "ResizeObserver loop completed with undelivered notifications.",
    "Request timeout",
    "Distributor.getValue",
    "getDictionariesByLanguageId",
    "Script error."
]
// Errors thrown by the browser itself, never by page code. "Permission denied to access <x>" is Gecko's
// cross-compartment security wrapper: we have no iframes and never await a cross-origin window, so the
// awaited object was handed to us from outside the page (extension content script / hardening shim).
const BrowserInternalPatterns = ["feature named", "window.__firefox__",
    "Permission denied to access property", "Permission denied to access object"]
const MonacoPatterns = ["monaco-editor", "vs/base/common/errors", "editor.main", "editor.worker"]
// A lazily-loaded chunk could not be fetched (transient network / CDN / deploy hiccup), not a logic bug.
// Cross-browser variants: Chrome/Edge, Firefox, Safari.
const ChunkLoadPatterns = [
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Importing a module script failed"
]
const ThirdPartyAppPatterns = ["_callback_receiveMIDIMessage", "_callback_addSource"]
const UrlPattern = /https?:\/\/[^\s)]+/g
// A stack frame that belongs to our code references a script module file: a built ".js"/".mjs"
// chunk in production, or a ".ts"/".tsx"/".jsx" source served by Vite in dev. The HTML document
// frame of an injected inline script (".../:line:col") matches none of these.
const ModuleUrlPattern = /\.(?:m?jsx?|tsx?)(?:[?:#]|$)/

export class ErrorHandler {
    readonly #terminator = new Terminator()
    readonly #buildInfo: BuildInfo
    readonly #recover: Provider<Option<Provider<Promise<void>>>>

    #errorThrown: boolean = false
    #rejectionReported: boolean = false
    #chunkLoadDialogOpen: boolean = false
    #browserInternalNotified: boolean = false

    constructor(buildInfo: BuildInfo, recover: Provider<Option<Provider<Promise<void>>>>) {
        this.#buildInfo = buildInfo
        this.#recover = recover
    }

    #looksLikeExtension(error: ErrorInfo): boolean {
        if (document.scripts.length > 1) {return true}
        if (ExtensionPatterns.some(pattern =>
            error.message?.includes(pattern) || error.stack?.includes(pattern))) {return true}
        // Safari content scripts (Vimari et al.) inject into an isolated world,
        // so document.scripts.length stays at 1. Their stacks carry function
        // names but no source URLs — our own minified bundle always emits URLs
        // in the stack, so a non-empty stack with zero URLs is a reliable
        // "this didn't come from our code" signal. (UrlPattern has the g flag,
        // so reuse .match() rather than .test() to avoid the lastIndex gotcha.)
        const stack = error.stack
        if (stack === undefined || stack.trim().length === 0) {return false}
        const urls = stack.match(UrlPattern)
        if (urls === null) {return true}
        // Injected/inline page scripts run from the document URL itself
        // (e.g. "@https://opendaw.studio/:4:46", "global code@.../:28:3"); our own
        // code only ever appears in frames that reference a script module file.
        // So a stack with source URLs but no module frame (the HTML document only)
        // did not originate in our code — generalises beyond any single injected id.
        return !urls.some(url => ModuleUrlPattern.test(url))
    }

    #extractForeignOrigin(error: ErrorInfo): string | null {
        const stack = error.stack
        if (stack === undefined) {return null}
        const urls = stack.match(UrlPattern) ?? []
        const expectedOrigin = window.location.origin
        for (const url of urls) {
            try {
                const origin = new URL(url).origin
                if (origin !== expectedOrigin) {return origin}
            } catch { /* invalid URL */ }
        }
        return null
    }

    #looksLikeMonacoError(message?: string, stack?: string, filename?: string): boolean {
        const sources = [message, stack, filename].filter(Boolean).join(" ")
        return MonacoPatterns.some(pattern => sources.includes(pattern))
    }

    // A lazy chunk (e.g. the code editor) failed to fetch. Only that component didn't open; the project
    // and the rest of the app are intact. Do NOT reload (that would discard unsaved work). Inform the user;
    // reopening the component retries the import. The guard only avoids STACKING duplicate dialogs from a
    // burst — it resets on dismiss, so a later failure notifies again.
    #notifyChunkLoadFailure(): void {
        if (this.#chunkLoadDialogOpen) {return}
        this.#chunkLoadDialogOpen = true
        Dialogs.info({
            headline: "Couldn't Load Component",
            message: "A part of openDAW failed to load, usually a temporary network issue. Your project is unaffected — please try again."
        }).finally(() => {this.#chunkLoadDialogOpen = false})
    }

    // The browser (or something injected into the page) threw, not openDAW, so the session continues. Tell the
    // user once per session: a shimmed API can throw on every call, and stacking dialogs would be worse.
    #notifyBrowserInternal(message: string): void {
        console.warn(`Browser internal error ignored: ${message}`)
        if (this.#browserInternalNotified || !Surface.isAvailable()) {return}
        this.#browserInternalNotified = true
        Dialogs.info({
            headline: "Warning",
            message: "Your browser or one of its extensions blocked an operation openDAW relies on. Consider disabling extensions or strict privacy settings for a more stable experience."
        }).then(EmptyExec)
    }

    #tryIgnore(event: Event): boolean {
        if (event instanceof ErrorEvent && IgnoredErrors.includes(event.message)) {
            console.warn(event.message)
            event.preventDefault()
            return true
        }
        if (event instanceof ErrorEvent
            && ThirdPartyAppPatterns.some(pattern => event.message.includes(pattern))) {
            console.warn(`Third-party app error ignored: ${event.message}`)
            event.preventDefault()
            return true
        }
        // Such an error carries no stack, so neither #looksLikeExtension nor #extractForeignOrigin can flag it
        // as foreign and it would reach the fatal path (#1105/#1106).
        if (event instanceof ErrorEvent
            && BrowserInternalPatterns.some(pattern => event.message.includes(pattern)
                || (event.error instanceof Error && event.error.message.includes(pattern)))) {
            event.preventDefault()
            this.#notifyBrowserInternal(event.message)
            return true
        }
        // Handle Monaco editor errors from error events
        // Monaco rethrows worker error Event objects through its error pipeline,
        // arriving as ErrorEvent where event.error is a raw Event (not an Error).
        if (event instanceof ErrorEvent
            && (this.#looksLikeMonacoError(event.message, event.error?.stack, event.filename)
                || event.error instanceof Event
                || event.message === "Uncaught [object Event]")) {
            console.warn("Monaco editor error:", event.message, event.filename)
            event.preventDefault()
            return true
        }
        if (event instanceof SecurityPolicyViolationEvent) {
            // Log CSP violations but don't crash - often caused by browser extensions or specific browser configs
            console.warn(`CSP violation: ${event.violatedDirective} blocked ${event.blockedURI}`)
            event.preventDefault()
            return true
        }
        // Resource/media LOAD failures (an <audio>/<video>/<img>/<script>/<link> whose source could not be
        // fetched or decoded) dispatch a plain "error" Event with the failing element as target — not an
        // ErrorEvent. They are not app crashes (e.g. a browser lacking the codec: "no supported source"); the
        // element's own onerror owns any UX. Ignore them so they do not fall through to the fatal path (#1022).
        if (event.type === "error" && !(event instanceof ErrorEvent) && event.target instanceof HTMLElement) {
            const detail = event.target instanceof HTMLMediaElement ? event.target.error?.message : undefined
            console.warn("Resource load error ignored:", detail ?? event.target.nodeName)
            event.preventDefault()
            return true
        }
        if (!(event instanceof PromiseRejectionEvent)) {return false}
        const {reason} = event
        if (ErrorInfo.isForeignRealmError(reason)) {
            event.preventDefault()
            this.#notifyBrowserInternal(`foreign realm rejection: ${ErrorInfo.extract(event).message}`)
            return true
        }
        const reasonMessage = reason instanceof Error ? reason.message : typeof reason === "string" ? reason : undefined
        if (isDefined(reasonMessage) && IgnoredErrors.some(ignored => reasonMessage.includes(ignored))) {
            console.warn(reasonMessage)
            event.preventDefault()
            return true
        }
        // A lazily-loaded chunk failed to fetch (transient network / CDN / deploy). Not a crash: a page
        // reload re-fetches it (and clears the browser's poisoned module map). Prompt instead of reporting.
        if (isDefined(reasonMessage) && ChunkLoadPatterns.some(pattern => reasonMessage.includes(pattern))) {
            console.warn(`Chunk load failed: ${reasonMessage}`)
            event.preventDefault()
            this.#notifyChunkLoadFailure()
            return true
        }
        if (Errors.isAbort(reason)) {
            console.debug(`Abort '${reason.message}'`)
            event.preventDefault()
            return true
        }
        if (reason instanceof Errors.Warning) {
            console.debug(`Warning '${reason.message}'`)
            event.preventDefault()
            Dialogs.info({headline: "Warning", message: reason.message}).then(EmptyExec)
            return true
        }
        // Handle SecurityError/NotAllowedError from File System Access API (e.g., showOpenFilePicker
        // denied, or called outside a live user gesture so transient activation was lost).
        if (reason instanceof DOMException && (reason.name === "SecurityError" || reason.name === "NotAllowedError")) {
            console.warn(`${reason.name}: ${reason.message}`)
            event.preventDefault()
            Dialogs.info({
                headline: "Access Denied",
                message: "The browser blocked access to the file system."
            }).then(EmptyExec)
            return true
        }
        // Storage full: an OPFS write exceeded the browser storage quota / the disk is full.
        // Environmental (not a logic bug); surface a friendly message instead of crashing.
        if (reason instanceof DOMException && reason.name === "QuotaExceededError") {
            console.warn(`QuotaExceededError: ${reason.message}`)
            event.preventDefault()
            Dialogs.info({
                headline: "Storage Full",
                message: "Your browser ran out of storage. Free up disk space or delete projects/samples, then try again."
            }).then(EmptyExec)
            return true
        }
        // Storage read failed: a transient OPFS/disk I/O read error. Environmental, not a logic bug.
        if (reason instanceof DOMException && reason.name === "NotReadableError") {
            console.warn(`NotReadableError: ${reason.message}`)
            event.preventDefault()
            Dialogs.info({
                headline: "Storage Error",
                message: "A storage read failed. This is usually a temporary disk or browser issue. Please try again."
            }).then(EmptyExec)
            return true
        }
        // Handle Monaco editor worker errors (throws Event objects when workers fail to load)
        if (reason instanceof Event || (reason instanceof Error && this.#looksLikeMonacoError(reason.message, reason.stack))) {
            console.warn("Monaco editor error (web workers may be unavailable):", reason)
            event.preventDefault()
            return true
        }
        // Handle Monaco CancellationError (name "Canceled" survives minification unlike stack traces)
        if (reason instanceof Error && reason.name === "Canceled") {
            console.debug(`Monaco CancellationError: ${reason.message}`)
            event.preventDefault()
            return true
        }
        // Handle browser-internal errors (e.g., DuckDuckGo feature detection)
        if (isDefined(reasonMessage)
            && BrowserInternalPatterns.some(pattern => reasonMessage.includes(pattern))) {
            event.preventDefault()
            this.#notifyBrowserInternal(reasonMessage)
            return true
        }
        return false
    }

    processError(scope: string, event: Event): boolean {
        if (this.#tryIgnore(event)) {return false}
        const error = ErrorInfo.extract(event)
        const foreignOrigin = this.#extractForeignOrigin(error)
        const looksLikeExtension = this.#looksLikeExtension(error) || foreignOrigin !== null
        console.warn("[ErrorHandler]", {
            scope,
            error,
            foreignOrigin,
            looksLikeExtension,
            scriptsCount: document.scripts.length,
            locationOrigin: window.location.origin
        })
        // Warn about extension errors but don't crash
        if (looksLikeExtension && !this.#errorThrown) {
            event.preventDefault()
            const originInfo = foreignOrigin !== null
                ? `This error originated from external code (${new URL(foreignOrigin).hostname}).`
                : "A browser extension may have caused an error."
            Dialogs.info({
                headline: "Warning",
                message: `${originInfo} Consider disabling extensions for a more stable experience.`
            }).then(EmptyExec)
            return false
        }
        console.debug("processError", scope, event)
        // An unhandled promise rejection means an async task failed; the main render loop is intact,
        // so it must NOT terminate the whole app. Report it once for visibility, then keep the
        // session alive (no AnimationFrame.terminate, no fatal recovery dialog). Synchronous "error"
        // events fall through to the fatal path below, since they can indicate corrupted state.
        if (event instanceof PromiseRejectionEvent) {
            event.preventDefault()
            if (!this.#rejectionReported) {
                this.#rejectionReported = true
                this.#report(scope, error)
            }
            return false
        }
        if (this.#errorThrown) {return false}
        this.#errorThrown = true
        AnimationFrame.terminate()
        this.#report(scope, error)
        this.#showDialog(scope, error, looksLikeExtension, foreignOrigin)
        return true
    }

    #report(scope: string, error: ErrorInfo): void {
        console.error(scope, error.name, error.message, error.stack)
        if (!import.meta.env.PROD) {return}
        const maxStackSize = 2000
        const body = JSON.stringify({
            date: new Date().toISOString(),
            agent: Browser.userAgent,
            build: this.#buildInfo,
            scripts: document.scripts.length,
            error: {...error, stack: error.stack?.slice(0, maxStackSize)},
            logs: LogBuffer.get()
        } satisfies ErrorLog)
        fetch("https://logs.opendaw.studio/log.php", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body
        }).then(console.info, console.warn)
    }

    #showDialog(scope: string, error: ErrorInfo, probablyHasExtension: boolean, foreignOrigin: string | null): void {
        if (Surface.isAvailable()) {
            Dialogs.error({
                scope,
                name: error.name,
                message: error.message ?? "no message",
                probablyHasExtension,
                foreignOrigin,
                backupCommand: this.#recover()
            })
        } else {
            alert(`Boot Error in '${scope}': ${error.name}`)
        }
    }

    install(owner: WindowProxy | Worker | AudioWorkletNode, scope: string): Terminable {
        if (this.#errorThrown) {return Terminable.Empty}
        const lifetime = this.#terminator.own(new Terminator())
        const handler = (event: Event) => {
            if (this.processError(scope, event)) {lifetime.terminate()}
        }
        lifetime.ownAll(
            Events.subscribe(owner, "error", handler),
            Events.subscribe(owner, "unhandledrejection", handler),
            Events.subscribe(owner, "messageerror", handler),
            Events.subscribe(owner, "processorerror" as any, handler),
            Events.subscribe(owner, "securitypolicyviolation", handler)
        )
        return lifetime
    }
}